/**
 * Enhanced AI Service for InsuraBot Integration
 * Part of PR #135 - Advanced AI capabilities for admin dashboard analytics
 */

import type {
  AIContextType,
  AIMessage,
  AIResponse,
  AIAnalytics,
  AIAdminInsights,
  CreateAISessionRequest,
  CreateAISessionResponse,
  SendAIMessageRequest,
  SendAIMessageResponse,
  AISessionSummary,
  AICapabilities,
} from "@/app/_module/types/ai-types";
import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall } from "../utils/apiResponseHandler";

class AIService {
  private sessionCache = new Map<string, CreateAISessionResponse>();
  private messageHistory = new Map<string, AIMessage[]>();

  /**
   * Create a new AI session with enhanced context awareness
   */
  async createSession(request: CreateAISessionRequest): Promise<CreateAISessionResponse> {
    try {
      const response = await handleApiCall(async () => await api.post<CreateAISessionResponse>({
        url: getApiUrl(endpoint.chatbot.createSession),
        body: {
          contextType: request.contextType,
          metadata: {
            ...request.metadata,
            timestamp: new Date().toISOString(),
            source: "insurabot-enhanced",
            version: "2.0.0"
          }
        },
        auth: true
      }));

      // Cache session for quick access
      this.sessionCache.set(response.sessionId, response);
      
      return response;
    } catch (error) {

      throw this.handleError(error);
    }
  }

  /**
   * Send message with enhanced context and analytics
   */
  async sendMessage(request: SendAIMessageRequest): Promise<SendAIMessageResponse> {
    try {
      // Validate session
      if (!request.sessionId || request.sessionId === 'undefined') {
        throw new Error('Invalid sessionId provided');
      }

      // Special handling for underwriting context - use N8N workflow
      if (request.contextType === 'underwriting') {
        return await this.handleUnderwritingMessage(request);
      }

      const response = await handleApiCall(async () => await api.post<SendAIMessageResponse>({
        url: getApiUrl(endpoint.chatbot.sendMessage),
        body: {
          message: request.message,
          sessionId: request.sessionId,
          contextType: request.contextType,
          includeHistory: request.includeHistory ?? true,
          metadata: {
            ...request.metadata,
            timestamp: new Date().toISOString(),
            messageLength: request.message.length
          }
        },
        auth: true
      }));

      // Update message history
      this.updateMessageHistory(request.sessionId, [
        {
          id: `user_${Date.now()}`,
          role: "user",
          content: request.message,
          timestamp: new Date(),
          metadata: request.metadata
        },
        {
          id: `assistant_${Date.now() + 1}`,
          role: "assistant",
          content: response.message,
          timestamp: new Date(),
          metadata: { usage: response.usage }
        }
      ]);

      return response;
    } catch (error) {

      throw this.handleError(error);
    }
  }

  /**
   * Get enhanced message history with analytics and universal context support
   */
  async getHistory(sessionId: string, contextType?: AIContextType): Promise<AIMessage[]> {
    try {
      if (!sessionId || sessionId === 'undefined') {
        throw new Error('Invalid sessionId provided');
      }

      // Check cache first
      const cacheKey = contextType ? `${sessionId}_${contextType}` : sessionId;
      const cached = this.messageHistory.get(cacheKey);
      if (cached) {
        return cached;
      }

      const url = contextType 
        ? `${getApiUrl(endpoint.chatbot.getHistoryFlat(sessionId))}?context=${contextType}`
        : getApiUrl(endpoint.chatbot.getHistoryFlat(sessionId));

      const response = await handleApiCall(async () => await api.get<AIMessage[]>({ url, auth: true }));

      const messages = Array.isArray(response) 
        ? response.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp || Date.now())
          }))
        : [];

      // Cache the history with context-aware key
      this.messageHistory.set(cacheKey, messages);
      
      return messages;
    } catch (error) {

      throw this.handleError(error);
    }
  }

  /**
   * Get AI analytics for admin dashboard
   */
  async getAnalytics(): Promise<AIAnalytics> {
    try {
      const response = await handleApiCall(async () => await api.get<AIAnalytics>({
        url: getApiUrl(endpoint.chatbot.getAnalytics),
        auth: true
      }));

      const payload: any = response;

      return {
        totalInteractions: payload?.totalInteractions ?? 0,
        averageResponseTime: payload?.averageResponseTime ?? 0,
        userSatisfactionScore: payload?.userSatisfactionScore ?? 0,
        commonQueries: payload?.commonQueries ?? [],
        peakUsageHours: payload?.peakUsageHours ?? []
      };
    } catch (error) {

      throw this.handleError(error);
    }
  }

  /**
   * Get admin-specific AI insights for dashboard metrics
   */
  async getAdminInsights(): Promise<AIAdminInsights> {
    try {
      const response = await handleApiCall(async () => await api.get<AIAdminInsights>({
        url: getApiUrl(`${endpoint.chatbot.getAnalytics}/admin-insights`),
        auth: true
      }));

      const payload: any = response;

      return {
        callAnalysis: {
          totalCalls: payload?.callAnalysis?.totalCalls ?? 0,
          averageDuration: payload?.callAnalysis?.averageDuration ?? 0,
          successRate: payload?.callAnalysis?.successRate ?? 0,
          trends: payload?.callAnalysis?.trends ?? [],
          scope: payload?.callAnalysis?.scope ?? 'agency'
        },
        leadInsights: {
          conversionRate: payload?.leadInsights?.conversionRate ?? 0,
          topSources: payload?.leadInsights?.topSources ?? [],
          qualityScore: payload?.leadInsights?.qualityScore ?? 0,
          scope: payload?.leadInsights?.scope ?? 'agency'
        },
        agencyMetrics: {
          performance: payload?.agencyMetrics?.performance ?? 0,
          growth: payload?.agencyMetrics?.growth ?? 0,
          efficiency: payload?.agencyMetrics?.efficiency ?? 0,
          teamSize: payload?.agencyMetrics?.teamSize ?? 0
        }
      };
    } catch (error) {

      // Return default values if API fails
      return {
        callAnalysis: {
          totalCalls: 0,
          averageDuration: 0,
          successRate: 0,
          trends: [],
          scope: 'agency'
        },
        leadInsights: {
          conversionRate: 0,
          topSources: [],
          qualityScore: 0,
          scope: 'agency'
        },
        agencyMetrics: {
          performance: 0,
          growth: 0,
          efficiency: 0,
          teamSize: 0
        }
      };
    }
  }

  /**
   * Handle underwriting messages using N8N workflow
   */
  private async handleUnderwritingMessage(request: SendAIMessageRequest): Promise<SendAIMessageResponse> {
    try {
      // Extract carrier information from metadata or message
      const carrierSlug = request.metadata?.carrier_slug || 'general';
      const product = request.metadata?.product || 'life_insurance';
      const state = request.metadata?.state || 'ALL';
      const applicant = request.metadata?.applicant || {};

      // Call N8N underwriting workflow
      const response = await handleApiCall(async () => await api.post<{
        success: boolean;
        message: string;
        data: {
          status: string;
          answer: string;
          carrier_slug: string;
          model_used: string;
          confidence_score: number;
          verification_results: {
            quality_check: any;
            accuracy_check: any;
          };
          context_quality: any;
          evidence_sources: Array<{
            doc_key: string;
            relevance_score: number;
            excerpt: string;
          }>;
          n8n_execution_id: string;
          processed_at: string;
        };
      }>({
        url: getApiUrl(endpoint.ai.askQuestion),
        body: {
          query: request.message,
          carrier_slug: carrierSlug,
          product: product,
          state: state,
          applicant: applicant
        },
        auth: true
      }));

      // Handle N8N response format
      if ((response as any).statusCode !== 200) {
        throw new Error((response as any).message || 'N8N workflow failed');
      }

      const n8nData = (response as any).data;

      // Update message history
      this.updateMessageHistory(request.sessionId, [
        {
          id: `user_${Date.now()}`,
          role: "user",
          content: request.message,
          timestamp: new Date(),
          metadata: request.metadata
        },
        {
          id: `assistant_${Date.now() + 1}`,
          role: "assistant",
          content: n8nData.answer,
          timestamp: new Date(),
          metadata: {
            usage: {
              model: n8nData.model_used,
              confidence: n8nData.confidence_score,
              n8n_execution_id: n8nData.n8n_execution_id
            },
            underwriting: {
              carrier: n8nData.carrier_slug,
              verification: n8nData.verification_results,
              evidence: n8nData.evidence_sources,
              context_quality: n8nData.context_quality
            }
          }
        }
      ]);

      return {
        message: n8nData.answer,
        sessionId: request.sessionId,
        usage: {
          inputTokens: 0,
          outputTokens: 0,
          totalTokens: 0
        },
        confidence: n8nData.confidence_score,
        sources: n8nData.evidence_sources?.map((source: any) => source.doc_key) || [],
        suggestions: this.generateUnderwritingSuggestions(n8nData.answer),
        followUpQuestions: this.generateUnderwritingFollowUps(request.message, n8nData.answer)
      };
    } catch (error) {

      throw this.handleError(error);
    }
  }

  /**
   * Generate underwriting-specific suggestions
   */
  private generateUnderwritingSuggestions(answer: string): string[] {
    const suggestions = [];
    
    if (answer.toLowerCase().includes('age')) {
      suggestions.push("What are the age requirements for different policy types?");
    }
    if (answer.toLowerCase().includes('health')) {
      suggestions.push("What medical conditions affect eligibility?");
    }
    if (answer.toLowerCase().includes('coverage')) {
      suggestions.push("What coverage amounts are available?");
    }
    if (answer.toLowerCase().includes('premium')) {
      suggestions.push("How are premiums calculated?");
    }
    
    return suggestions.slice(0, 3); // Limit to 3 suggestions
  }

  /**
   * Generate underwriting-specific follow-up questions
   */
  private generateUnderwritingFollowUps(originalQuery: string, answer: string): string[] {
    const followUps = [];
    
    if (originalQuery.toLowerCase().includes('eligibility')) {
      followUps.push("What documents are required for underwriting?");
      followUps.push("How long does the underwriting process take?");
    }
    if (originalQuery.toLowerCase().includes('requirements')) {
      followUps.push("Are there any exceptions to these requirements?");
      followUps.push("What if I don't meet all requirements?");
    }
    if (originalQuery.toLowerCase().includes('policy')) {
      followUps.push("What are the different policy types available?");
      followUps.push("Can I change my policy after approval?");
    }
    
    return followUps.slice(0, 2); // Limit to 2 follow-ups
  }

  /**
   * Ingest underwriting document using N8N workflow
   */
  async ingestUnderwritingDocument(data: {
    url: string;
    carrier_slug?: string;
    eff_date?: string;
    version?: string;
    state_scope?: string[];
  }): Promise<{
    success: boolean;
    message: string;
    data: {
      status: string;
      doc_key: string;
      carrier_slug: string;
      security_verification: {
        layer1_freshness: any;
        layer2_accuracy: any;
        layer4_business: any;
        layer5_final: any;
      };
      document_stats: {
        text_length: number;
        word_count: number;
        extraction_method: string;
      };
    };
  }> {
    try {
      const response = await handleApiCall(async () => await api.post<{
        statusCode: number;
        message: string;
        data: {
          status: string;
          doc_key: string;
          carrier_slug: string;
          auto_generated: boolean;
          n8n_execution_id: string;
          security_verification: {
            layer1_freshness: any;
            layer2_accuracy: any;
            layer4_business: any;
            layer5_final: any;
          };
          document_stats: {
            text_length: number;
            word_count: number;
            extraction_method: string;
          };
          processed_at: string;
        };
        timestamp: string;
      }>({
        url: getApiUrl(endpoint.ai.ingestDocument),
        body: data
      }));

      // Handle standardized response format
      if (response.statusCode !== 200 && response.statusCode !== 201) {
        throw new Error(response.message || 'Document ingestion failed');
      }

      return {
        success: true,
        message: response.message,
        data: response.data
      };
    } catch (error) {

      throw this.handleError(error);
    }
  }

  /**
   * End AI session and cleanup
   */
  async endSession(sessionId: string): Promise<void> {
    try {
      await handleApiCall(async () => await api.post({
        url: getApiUrl(endpoint.chatbot.endSession(sessionId)),
        body: { endReason: "user_initiated" },
        auth: true
      }));

      // Cleanup caches
      this.sessionCache.delete(sessionId);
      this.messageHistory.delete(sessionId);
    } catch (error) {

      throw this.handleError(error);
    }
  }

  /**
   * Universal context switcher - supports all context types dynamically
   */
  async switchContext(sessionId: string, newContext: AIContextType): Promise<void> {
    try {
      await handleApiCall(async () => await api.post({
        url: getApiUrl(`${endpoint.chatbot.switchContext}/${sessionId}`),
        body: { contextType: newContext },
        auth: true
      }));
      
      // Update cached session context if it exists
      const cachedSession = this.sessionCache.get(sessionId);
      if (cachedSession) {
        cachedSession.contextType = newContext;
        this.sessionCache.set(sessionId, cachedSession);
      }
    } catch (error) {

      throw this.handleError(error);
    }
  }

  /**
   * Get all available contexts universally
   */
  async getAllContexts(): Promise<AIContextType[]> {
    try {
      const capabilities = await this.getCapabilities();
      return capabilities.contexts;
    } catch (error) {

      // Return default universal contexts
      return ["general", "underwriting", "carrier_mapping", "admin_analytics", "custom"];
    }
  }
  async getCapabilities(): Promise<AICapabilities> {
    try {
      const response = await handleApiCall(async () => await api.get<AICapabilities>({
        url: getApiUrl(`${endpoint.chatbot.getAnalytics}/capabilities`),
        auth: true
      }));

      return {
        contexts: response.contexts || ["general", "underwriting", "carrier_mapping", "admin_analytics"],
        features: response.features || ["chat", "analytics", "insights", "redaction"],
        limitations: response.limitations || ["rate_limited", "context_window"],
        version: response.version || "2.0.0"
      };
    } catch (error) {

      // Return default capabilities
      return {
        contexts: ["general", "underwriting", "carrier_mapping", "admin_analytics"],
        features: ["chat", "analytics", "insights"],
        limitations: ["rate_limited"],
        version: "2.0.0"
      };
    }
  }

  /**
   * List recent AI sessions
   */
  async listSessions(limit = 20): Promise<AISessionSummary[]> {
    try {
      const response = await handleApiCall(async () => await api.get<AISessionSummary[]>({
        url: getApiUrl(`${endpoint.chatbot.listSessions}?limit=${limit}`),
        auth: true
      }));

      return Array.isArray(response) ? response : [];
    } catch (error) {

      throw this.handleError(error);
    }
  }

  /**
   * Clear cached data
   */
  clearCache(): void {
    this.sessionCache.clear();
    this.messageHistory.clear();
  }

  /**
   * Update message history cache
   */
  private updateMessageHistory(sessionId: string, newMessages: AIMessage[]): void {
    const existing = this.messageHistory.get(sessionId) || [];
    this.messageHistory.set(sessionId, [...existing, ...newMessages]);
  }

  /**
   * Handle API errors with enhanced error information
   */
  private handleError(error: unknown): Error {
    if (error && typeof error === 'object' && 'response' in error) {
      const apiError = error as { response?: { status?: number; data?: { message?: string } } };
      const status = apiError.response?.status;
      const message = apiError.response?.data?.message;

      if (status === 429) {
        return new Error(`Rate limit exceeded. ${message || 'Please try again later.'}`);
      }
      if (status === 400) {
        return new Error(`Invalid request. ${message || 'Please check your input.'}`);
      }
      if (status === 401) {
        return new Error(`Authentication failed. ${message || 'Please sign in again.'}`);
      }
      if (status === 500) {
        return new Error(`Server error. ${message || 'Please try again later.'}`);
      }
    }

    if (error instanceof Error) {
      return error;
    }

    return new Error(`AI service error: ${String(error)}`);
  }
}

// Export singleton instance
export const aiService = new AIService();
export default aiService;